import React, { useEffect, useState } from 'react';
import { IFreelancer } from '../../data_models/freelancer';
import { IEmployer } from '../../data_models/employer';
import SmallAvatar from '../common/SmallAvatar';
import { DEFAULT_USER_PROFILE } from '../../config/defaultAssetUrls';
import { getFreelancerName } from '../../utils/freelancerUtils';
import { getEmployerName } from '../../utils/employerUtils';
import useChat from '../../hooks/useChat';

import SearchBar from '../common/SearchBar';

import useUser from '../../hooks/useUser';
import { UserEnumTextMapper } from '../../data_models/enumToTextMapper';
import { EUserType } from '../../data_models/enums';
import { ChatOneToOneConversation } from '../../data_models/chat';

const ContactList: React.FC = ({ }) => {

    const {
        loading,
        oneToOneConversations,
        chatPartners,
        conversationsWithUnreadMessages,
        totalNumberUnreadMessages,
        activeConversation,
        activeMessages,
        activeChatPartner,
        typing,
        getConversationIdByPartnerId,
        setActiveConversationByPartner,
        unselectActiveChat
    } = useChat();

    const { searchFreelancers: _searchFreelancers } = useUser();

    const [sortedChatPartners, setSortedChatPartners] = useState<(IEmployer | IFreelancer)[]>([]);




    useEffect(() => {
        const chatPartnerList: (IEmployer | IFreelancer)[] = [];

        oneToOneConversations?.forEach((conversation) => {
            const partner = chatPartners?.get(conversation._id!);
            if (partner) {
                chatPartnerList.push(partner);
            }
        });

        if (!activeConversation && !activeChatPartner && (oneToOneConversations?.length || 0) > 0) {
            const partner = chatPartners?.get(oneToOneConversations![0]._id!);
            if (partner) {
                setActiveConversationByPartner(partner);
            }
        }

        setSortedChatPartners(chatPartnerList);
    }, [chatPartners, oneToOneConversations]);

    function isEmployer(chatPartner: any): chatPartner is IEmployer {
        return "employerName" in chatPartner;
    }

    function isActive(chatPartner: IEmployer | IFreelancer): boolean {
        return chatPartner._id === activeChatPartner?._id && isEmployer(chatPartner) === isEmployer(activeChatPartner);
    }

    const searchFreelancers = async (searchTerm: string) => {
        return await _searchFreelancers(searchTerm, 10);
    }

    const hasUnreadMessages = (chatPartner: IEmployer | IFreelancer): boolean => {
        const conversationId = getConversationIdByPartnerId(chatPartner._id!);
        if (!conversationId) return false;
        return (conversationsWithUnreadMessages.get(conversationId) || 0) > 0;
    }

    const getUnreadMessagesCount = (chatPartner: IEmployer | IFreelancer): number => {
        const conversationId = getConversationIdByPartnerId(chatPartner._id!);
        if (!conversationId) return 0;
        return conversationsWithUnreadMessages.get(conversationId) || 0;
    }

    const isTyping = (chatPartner: IEmployer | IFreelancer): boolean => {
        const conversationId = getConversationIdByPartnerId(chatPartner._id!);
        if (!conversationId) return false;

        const typingUsers = typing.get(conversationId) || [];
        return typingUsers.some(user => user.id === chatPartner._id && user.userType === (isEmployer(chatPartner) ? EUserType.EMPLOYER : EUserType.FREELANCER));
    }

    const getConversation = (chatPartner: IEmployer | IFreelancer): ChatOneToOneConversation | undefined => {
        return oneToOneConversations?.find(conversation => conversation._id === getConversationIdByPartnerId(chatPartner._id!));
    }

    const onClickSearchResult = (freelancer: IFreelancer) => {
        unselectActiveChat();
        setActiveConversationByPartner(freelancer);
    }

    // Function to render each search result
    const renderSearchResult = (freelancer: IFreelancer): React.ReactNode => {
        return (
            <div className="p-2 rounded-md hover:bg-neutral-100 cursor-pointer flex flex-row items-center space-x-4"
                onClick={() => { onClickSearchResult(freelancer) }}
            >
                <SmallAvatar
                    imageUrl={freelancer.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE}
                />
                <div className='flex flex-col'>
                    <p className='caption2 font-bold'>{getFreelancerName(freelancer)}</p>
                    <div className='flex flex-wrap gap-x-2'>{freelancer.roles &&
                        freelancer.roles!.map((role, index) =>
                            <div key={role} className='flex items-center gap-1'>
                                <p className='caption2'>{UserEnumTextMapper.freelancerRole(role)}</p>
                                {index < (freelancer.roles!.length - 1) && <div className='w-1 h-1 rounded-full bg-neutral-400' />}
                            </div>
                        )}
                    </div>
                </div>
            </div>)
    };

    return (
        <div className='flex flex-col h-full p-2'>
            {/* Search bar */}
            <div className=''>
                <SearchBar
                    searchFunction={searchFreelancers}
                    renderResult={renderSearchResult}
                    placeholder="Search Contacts..."
                />
            </div>
            {/* Contact list empty */}
            {sortedChatPartners.length === 0 &&
                <p className='mt-4 caption1 text-center'>No Recent Contacts</p>
            }


            {/* Contact list not empty */}
            {sortedChatPartners.length > 0 && <div className='overflow-y-auto h-full py-2'>
                <div className='flex flex-col space-y-1'>
                    {sortedChatPartners.map((chatPartner) =>
                        <div key={chatPartner._id} className={`p-2 flex items-center space-x-4 cursor-pointer  rounded-xl
                        ${isActive(chatPartner) ? 'bg-neutral-300' : 'hover:bg-neutral-100'}`}
                            onClick={() => { setActiveConversationByPartner(chatPartner) }}
                        >

                            <SmallAvatar

                                imageUrl={isEmployer(chatPartner)
                                    ? chatPartner.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_EMPLOYER_PROFILE_PICTURE
                                    : chatPartner.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE
                                }
                            />
                            <div className='flex flex-col'>
                                <p className='caption2 font-bold'>{isEmployer(chatPartner)
                                    ? getEmployerName(chatPartner as IEmployer)
                                    : getFreelancerName(chatPartner as IFreelancer)}</p>
                                {/* <div className='flex flex-wrap gap-x-2'>{!isEmployer(chatPartner) && (chatPartner as IFreelancer).roles &&
                                    (chatPartner as IFreelancer).roles!.map((role, index) =>
                                        <div key={role} className='flex items-center gap-1'>
                                            <p className='caption2'>{UserEnumTextMapper.freelancerRole(role)}</p>
                                            {index < ((chatPartner as IFreelancer).roles!.length - 1) && <div className='w-1 h-1 rounded-full bg-neutral-400' />}
                                        </div>
                                    )}
                                </div> */}
                                {isTyping(chatPartner)
                                    ? <div className='caption2 text-neutral-700 italic'>
                                        typing...
                                    </div>
                                    : getConversation(chatPartner)?.lastMessageContent
                                        ? <div className='max-w-40 caption2 text-neutral-700 line-clamp-1'>
                                            {getConversation(chatPartner)?.lastMessageContent
                                                ? getConversation(chatPartner)?.lastMessageContent
                                                : getConversation(chatPartner)?.lastMessageFileMeta?.filename
                                                    ? getConversation(chatPartner)?.lastMessageFileMeta?.filename
                                                    : 'File'}
                                        </div>
                                        : null
                                }
                            </div>
                            {hasUnreadMessages(chatPartner) &&
                                <div className='px-3 rounded-full bg-primary text-white'>
                                    {getUnreadMessagesCount(chatPartner)}
                                </div >}
                        </div>
                    )}
                </div>
            </div>}
        </div>
    );
};

export default ContactList;
